import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Check, X, Bell, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';
import { api, fmtDate, fmtMoney } from '../lib/api';
import { PriceDeltaBadge, OldNewPricePills } from '../components/PriceDeltaBadge';

export default function AlertsPage() {
    const { refreshAlerts } = useOutletContext() || {};
    const [tab, setTab] = useState('all');
    const [rows, setRows] = useState(null);
    const [confirmBulk, setConfirmBulk] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);

    async function load() {
        setRows(null);
        const params = {};
        if (tab === 'increases') params.direction = 'increase';
        else if (tab === 'decreases') params.direction = 'decrease';
        else if (tab === 'dismissed') params.include_dismissed = true;
        const data = await api.listAlerts(params);
        const filtered = tab === 'dismissed' ? data.filter((a) => a.dismissed) : data;
        setRows(filtered);
    }

    useEffect(() => {
        load(); // eslint-disable-next-line
    }, [tab]);

    async function dismiss(id) {
        await api.dismissAlert(id);
        toast.success('Alert dismissed');
        refreshAlerts?.();
        load();
    }
    async function restore(id) {
        await api.restoreAlert(id);
        toast.success('Alert restored');
        refreshAlerts?.();
        load();
    }
    async function dismissAllVisible() {
        setBulkBusy(true);
        try {
            const params = {};
            if (tab === 'increases') params.direction = 'increase';
            else if (tab === 'decreases') params.direction = 'decrease';
            // "Dismissed" tab is a no-op for bulk-dismiss (nothing to hide)
            const res = await api.dismissVisibleAlerts(params);
            const n = res?.dismissed || 0;
            toast.success(n > 0 ? `Dismissed ${n} alert${n !== 1 ? 's' : ''}` : 'Nothing to dismiss');
            setConfirmBulk(false);
            refreshAlerts?.();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Bulk dismiss failed');
        } finally {
            setBulkBusy(false);
        }
    }
    const showBulkBtn = tab !== 'dismissed' && rows && rows.length > 0;

    return (
        <div className="space-y-5 animate-fade-in">
            <PageHeader
                title="Price alerts"
                subtitle="Detected whenever an item unit price differs from the last bill you had from the same vendor."
                actions={
                    showBulkBtn ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmBulk(true)}
                            disabled={bulkBusy}
                            data-testid="dismiss-all-visible-btn"
                        >
                            <Check size={13} className="mr-1" />
                            Dismiss all ({rows.length})
                        </Button>
                    ) : null
                }
            />

            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList data-testid="alerts-tabs">
                    <TabsTrigger value="all" data-testid="alerts-tab-all">All</TabsTrigger>
                    <TabsTrigger value="increases" data-testid="alerts-tab-increases">Increases</TabsTrigger>
                    <TabsTrigger value="decreases" data-testid="alerts-tab-decreases">Decreases</TabsTrigger>
                    <TabsTrigger value="dismissed" data-testid="alerts-tab-dismissed">Dismissed</TabsTrigger>
                </TabsList>
            </Tabs>

            <Card className="p-0 border-border/80 overflow-hidden">
                {rows === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                            <Bell size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">No alerts</div>
                        <div className="text-sm text-muted-foreground">You’re all caught up.</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="alerts-table">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-3 font-medium">Item</th>
                                    <th className="text-left px-4 py-3 font-medium">Vendor</th>
                                    <th className="text-left px-4 py-3 font-medium">Change</th>
                                    <th className="text-left px-4 py-3 font-medium">Delta</th>
                                    <th className="text-left px-4 py-3 font-medium">Prev bill date</th>
                                    <th className="text-left px-4 py-3 font-medium">New bill date</th>
                                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((a) => (
                                    <tr key={a.id} className="border-t border-border hover:bg-muted/40">
                                        <td className="px-4 py-3 font-medium">{a.item_name}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{a.vendor}</td>
                                        <td className="px-4 py-3">
                                            <OldNewPricePills oldPrice={a.previous_price} newPrice={a.new_price} />
                                            {a.tax_inclusive && (
                                                <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground" data-testid={`alerts-gst-tag-${a.id}`}>
                                                    incl. GST
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <PriceDeltaBadge delta={a.delta} deltaPercent={a.delta_percent} />
                                        </td>
                                        <td className="px-4 py-3 text-muted-foreground">{fmtDate(a.previous_bill_date)}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{fmtDate(a.new_bill_date)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center gap-2 justify-end">
                                                <Link to={`/bills/${a.bill_id}`} className="text-primary hover:underline text-xs" data-testid={`alerts-open-bill-${a.id}`}>
                                                    open bill
                                                </Link>
                                                {a.dismissed ? (
                                                    <Button size="sm" variant="outline" onClick={() => restore(a.id)} data-testid={`alerts-restore-${a.id}`}>
                                                        Restore
                                                    </Button>
                                                ) : (
                                                    <Button size="sm" variant="outline" onClick={() => dismiss(a.id)} data-testid={`alerts-dismiss-${a.id}`}>
                                                        Dismiss
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <AlertDialog open={confirmBulk} onOpenChange={(o) => !bulkBusy && setConfirmBulk(o)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Dismiss {rows?.length || 0} alert{(rows?.length || 0) !== 1 ? 's' : ''}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This dismisses only the alerts currently visible on this tab.
                            You can restore any of them from the "Dismissed" tab later.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={dismissAllVisible}
                            disabled={bulkBusy}
                            data-testid="confirm-dismiss-all-btn"
                        >
                            {bulkBusy && <Loader2 size={12} className="mr-1 animate-spin" />}
                            Dismiss all
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
